import React from 'react'
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
} from '@mui/material'
import { fontSizes, fontWeights } from '../../../Common/Typography/TypographyStyles'
import {
  selectTaskCompletionMetrics,
  selectAssignedTeamMemberReport,
} from '../../../../redux/reports/task/assignedTeamMemberPerTask/selector'
import { useSelector } from 'react-redux'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import SupervisorDrillDownChart from './SupervisorDrillDownChart'

// ─── Metric Card ────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string
  value: string | number
  subLabel?: string
  background: string
  Icon: React.ElementType
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, subLabel, background, Icon }) => (
  <Card
    sx={{
      background,
      color: 'white',
      borderRadius: '12px',
      width: '100%',
    }}
    elevation={2}
  >
    <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
        <Typography
          sx={{
            fontSize: fontSizes.size14,
            fontWeight: fontWeights[500],
            textShadow: '1px 1px 3px rgba(0,0,0,0.4)',
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        <Icon sx={{ fontSize: '36px', opacity: 0.9 }} />
      </Box>
      <Typography
        sx={{
          fontSize: fontSizes.size36,
          fontWeight: fontWeights[700],
          textShadow: '1px 1px 3px rgba(0,0,0,0.4)',
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      {subLabel && (
        <Typography sx={{ fontSize: fontSizes.size13, opacity: 0.85, mt: 0.75 }}>
          {subLabel}
        </Typography>
      )}
    </CardContent>
  </Card>
)

// ─── Main Component ──────────────────────────────────────────────────────────

const TaskParticipantChart: React.FC = () => {
  const metrics = useSelector(selectTaskCompletionMetrics)
  const taskDetails = useSelector(selectAssignedTeamMemberReport)

  const { onTimeCompletionRate, overdue, pending, totalAssigned } = metrics

  const hasData = totalAssigned > 0

  const rawDueDate = taskDetails?.assignedTeamMembers?.[0]?.dueDate
  const formatDueDate = (date: string | Date): string => {
    const d = new Date(date)
    const formatted = d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    return formatted
  }
  const dueDateLabel = rawDueDate ? `Due Date: ${formatDueDate(rawDueDate)}` : ''

  return (
    <Card elevation={0}>
      <CardHeader
        title={
          <Typography sx={{ fontSize: fontSizes.size16, fontWeight: fontWeights[700] }}>
            Task Completion Report
          </Typography>
        }
        subheader={
          <Box display='flex' flexDirection='column'>
            <Typography variant='body2' color='text.secondary'>
              {taskDetails?.taskTitle ?? ''}
            </Typography>
            {dueDateLabel && (
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2.5 }}>
                {dueDateLabel}
              </Typography>
            )}
          </Box>
        }
        sx={{ pb: 0, pt: 1 }}
      />

      <CardContent sx={{ pt: 1 }}>
        {/* ── Main two-column layout ───────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            alignItems: 'center',
          }}
        >
          {/* ── LEFT: 2 stacked metric cards ──────────────────────────── */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              flex: '0 0 auto',
              width: { xs: '100%', md: '380px' },
              ml: { md: 5 },
            }}
          >
            <MetricCard
              label='On-Time Completion Rate'
              value={`${onTimeCompletionRate}%`}
              subLabel={`${hasData ? Math.round((onTimeCompletionRate / 100) * totalAssigned) : 0} of ${totalAssigned} completed on time`}
              background='linear-gradient(135deg, #9ebd13 0%, #008552 100%)'
              Icon={AccessTimeIcon}
            />
            <MetricCard
              label='Overdue Rate'
              value={`${overdue.percentage}%`}
              subLabel={`${overdue.count} of ${totalAssigned} tasks are overdue`}
              background='linear-gradient(135deg, #d53369 0%, #cbad6d 100%)'
              Icon={WarningAmberIcon}
            />
            <MetricCard
              label='No. of Pending Tasks'
              value={pending.count}
              subLabel={`Not Started or In Progress`}
              background='linear-gradient(135deg, #f7971e 0%, #ffd200 100%)'
              Icon={PendingActionsIcon}
            />
          </Box>

          {/* ── RIGHT: Drill-Down Chart ────────────────────────────────── */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {hasData ? (
              <SupervisorDrillDownChart />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: 'text.secondary' }}>
                <Typography variant='body2'>No task data available.</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default TaskParticipantChart
